<template>
  <div class="section-wrapper">
    <h3 class="section-header">School, Grade, and Subject (Optional)</h3>

    <div v-if="isEditing" class="edit-school-container">
      <div class="school-inputs-container">
        <SchoolSelectionInput v-model="selectedSchoolObject" label="School" />
        <div class="input-group">
          <p class="grade-label">Grade</p>
          <div class="chips-container">
            <template v-if="isLoadingGrades">
              <div
                v-for="n in 6"
                :key="`grade-skeleton-${n}`"
                class="chip-skeleton"
              ></div>
            </template>
            <template v-else>
              <button
                v-for="option in gradeOptions"
                :key="option.value"
                class="chip-button"
                :class="{ selected: selectedGrade.includes(option.value) }"
                @click="toggleGradeSelection(option.value)"
              >
                {{ option.text }}
              </button>
            </template>
          </div>
        </div>
        <div class="input-group">
          <p class="grade-label">Subject</p>
          <div class="chips-container">
            <template v-if="isLoadingSubjects">
              <div
                v-for="n in 6"
                :key="`subject-skeleton-${n}`"
                class="chip-skeleton"
              ></div>
            </template>
            <template v-else>
              <button
                v-for="option in subjectOptions"
                :key="option.value"
                class="chip-button"
                :class="{
                  selected: selectedSubject.includes(option.value),
                }"
                @click="toggleSubjectSelection(option.value)"
              >
                {{ option.text }}
              </button>
            </template>
          </div>
        </div>
      </div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </div>
    <div v-else class="display-container">
      <div class="info-field">
        <p class="info-label">School</p>
        <p class="info-value">
          {{ user?.school || "None" }}
        </p>
      </div>
      <div class="info-field">
        <p class="info-label">Grade</p>
        <p class="info-value">
          {{ getGradeLabel(user?.grades) || "None" }}
        </p>
      </div>
      <div class="info-field">
        <p class="info-label">Subject</p>
        <p class="info-value">
          {{ getSubjectLabel(user?.subjects) || "None" }}
        </p>
      </div>
    </div>

    <ConfirmationModal
      v-model="showLocationChangeModal"
      title="Confirmation"
      description="Changing to a school outside your region will reset your grade and subject selections. Proceed?"
      confirm-text="Proceed"
      cancel-text="Back"
      @confirm="handleConfirmLocationChange"
      @cancel="handleCancelLocationChange"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, watch } from "vue";
import SchoolSelectionInput from "../common/SchoolSelectionInput.vue";
import ConfirmationModal from "../common/ConfirmationModal.vue";
import categoryAdminService from "@/services/categoryAdminService";
import categoryService from "@/services/categoryService";

const props = defineProps({
  user: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["editStarted"]);

const school = ref("");
const selectedSchoolObject = ref(null);
const errorMessage = ref(null);

const selectedGrade = ref([]);
const selectedSubject = ref([]);
const gradeOptions = ref([]);
const subjectOptions = ref([]);
const isLoadingGrades = ref(false);
const isLoadingSubjects = ref(false);

const showLocationChangeModal = ref(false);
const pendingSchoolObject = ref(null);
const previousSchoolObject = ref(null);
const previousGrades = ref([]);
const previousSubjects = ref([]);
const isRevertingSchool = ref(false);

const currentValidSchoolObject = ref(null);
const regionSelectionsCache = ref({});

const getRegionCode = (location) => {
  if (!location) return "";
  const normalized = String(location).toUpperCase().trim();
  if (normalized === "SG" || normalized === "SINGAPORE") return "SG";
  if (
    normalized === "US" ||
    normalized === "USA" ||
    normalized === "UNITED STATES"
  )
    return "US";
  if (normalized === "ID" || normalized === "INDONESIA") return "ID";
  return "US";
};

const normalizeLocation = (location) => {
  if (!location) return "US";
  const normalized = String(location).toUpperCase().trim();
  if (normalized === "SG" || normalized === "SINGAPORE") {
    return "SG";
  }
  return "US";
};

const checkLocationChange = (newSchoolLocation, oldSchoolLocation) => {
  if (!newSchoolLocation && !oldSchoolLocation) return false;

  const normNew = normalizeLocation(newSchoolLocation);
  const normOld = normalizeLocation(oldSchoolLocation);

  return normNew !== normOld;
};

const getSchoolLocale = () => {
  const rawLocation =
    selectedSchoolObject.value?.location ||
    selectedSchoolObject.value?.country ||
    "US";
  return normalizeLocation(rawLocation).toLowerCase();
};

const loadTermOptions = async (search) => {
  const categoryResponse = await categoryAdminService.getAll({ search });
  const category = categoryResponse?.data?.data?.rows?.[0];
  if (!category) return [];

  const termsResponse = await categoryService.getTermsDropdown({
    taxonomy: category.id,
    locale: getSchoolLocale(),
    sort_by: "priority",
    order: "asc",
  });
  const terms = termsResponse?.data?.data?.rows || [];

  terms.sort((a, b) => {
    const priorityA = a.priority ?? Infinity;
    const priorityB = b.priority ?? Infinity;
    return priorityA - priorityB;
  });

  return terms.map((term) => ({
    value: term.id,
    text: term.title,
  }));
};

const loadSubjectOptions = async () => {
  isLoadingSubjects.value = true;
  try {
    subjectOptions.value = await loadTermOptions("subject");
  } catch (error) {
    console.error("Error loading subject options:", error);
  } finally {
    isLoadingSubjects.value = false;
  }
};

const loadGradeOptions = async () => {
  isLoadingGrades.value = true;
  try {
    gradeOptions.value = await loadTermOptions("grade");
  } catch (error) {
    console.error("Error loading grade options:", error);
  } finally {
    isLoadingGrades.value = false;
  }
};

const getSubjectLabelFromId = (id) => {
  const option = subjectOptions.value.find((o) => o.value === id);
  return option ? option.text : null;
};

const getGradeLabelFromId = (id) => {
  const option = gradeOptions.value.find((o) => o.value === id);
  return option ? option.text : null;
};

const getGradeLabel = (grades) => {
  if (Array.isArray(grades) && grades.length > 0) {
    return grades.join(", ");
  }
  if (Array.isArray(grades) && grades.length === 0) return "None";
  return grades || "None";
};

const getSubjectLabel = (subjects) => {
  if (Array.isArray(subjects) && subjects.length > 0) {
    return subjects.join(", ");
  }
  if (Array.isArray(subjects) && subjects.length === 0) return "None";
  return subjects || "None";
};

const toggleGradeSelection = (gradeId) => {
  const index = selectedGrade.value.indexOf(gradeId);
  if (index === -1) {
    selectedGrade.value.push(gradeId);
  } else {
    selectedGrade.value.splice(index, 1);
  }
};

const toggleSubjectSelection = (subjectId) => {
  const index = selectedSubject.value.indexOf(subjectId);
  if (index === -1) {
    selectedSubject.value.push(subjectId);
  } else {
    selectedSubject.value.splice(index, 1);
  }
};

// Watch selectedSchoolObject for region changes
watch(selectedSchoolObject, async (newValue) => {
  if (isRevertingSchool.value) {
    isRevertingSchool.value = false;
    return;
  }

  // Case 1: Cleared input
  if (!newValue) {
    school.value = null;
    return;
  }
  // Case 2: Selected a school
  const newLocation = newValue.location || newValue.country;
  const oldLocation =
    currentValidSchoolObject.value?.location ||
    currentValidSchoolObject.value?.country;

  // Check if it's the exact same school (optimization)
  const isSameSchool =
    currentValidSchoolObject.value &&
    currentValidSchoolObject.value.name &&
    newValue.name &&
    currentValidSchoolObject.value.name === newValue.name &&
    !checkLocationChange(newLocation, oldLocation);

  if (isSameSchool) {
    return;
  }

  // Check for Region Change
  if (checkLocationChange(newLocation, oldLocation)) {
    // REGION CHANGED -> Trigger Modal
    pendingSchoolObject.value = newValue;
    previousSchoolObject.value = currentValidSchoolObject.value;
    previousGrades.value = [...selectedGrade.value];
    previousSubjects.value = [...selectedSubject.value];
    showLocationChangeModal.value = true;
    return;
  }

  // NO REGION CHANGE -> Just update
  school.value = newValue.name;

  // Update the valid object anchor
  currentValidSchoolObject.value = newValue;

  const preservedGradeLabels = selectedGrade.value
    .map((id) => getGradeLabelFromId(id))
    .filter((label) => label !== null);
  const preservedSubjectLabels = selectedSubject.value
    .map((id) => getSubjectLabelFromId(id))
    .filter((label) => label !== null);

  subjectOptions.value = [];
  gradeOptions.value = [];
  await Promise.all([loadSubjectOptions(), loadGradeOptions()]);

  // Restore selections
  if (preservedGradeLabels.length > 0) {
    const restoredGradeIds = preservedGradeLabels
      .map((label) => {
        const grade = gradeOptions.value.find((g) => g.text === label);
        return grade ? grade.value : null;
      })
      .filter((id) => id !== null);
    selectedGrade.value = restoredGradeIds;
  }

  if (preservedSubjectLabels.length > 0) {
    const restoredSubjectIds = preservedSubjectLabels
      .map((label) => {
        const subject = subjectOptions.value.find((s) => s.text === label);
        return subject ? subject.value : null;
      })
      .filter((id) => id !== null);
    selectedSubject.value = restoredSubjectIds;
  }
});

const handleConfirmLocationChange = async () => {
  showLocationChangeModal.value = false;

  if (pendingSchoolObject.value) {
    // 1. Identify Regions
    const oldRegion = getRegionCode(
      currentValidSchoolObject.value?.location ||
        currentValidSchoolObject.value?.country
    );
    const newRegion = getRegionCode(
      pendingSchoolObject.value.location || pendingSchoolObject.value.country
    );

    // 2. Save current selections to cache (snapshot of old region state)
    regionSelectionsCache.value[oldRegion] = {
      grades: [...selectedGrade.value],
      subjects: [...selectedSubject.value],
    };

    school.value = pendingSchoolObject.value.name;

    // Update the valid object anchor since we confirmed the change
    currentValidSchoolObject.value = pendingSchoolObject.value;

    selectedGrade.value = [];
    selectedSubject.value = [];
    subjectOptions.value = [];
    gradeOptions.value = [];
    await Promise.all([loadSubjectOptions(), loadGradeOptions()]);

    // 3. Restore from cache if exists for new region
    if (regionSelectionsCache.value[newRegion]) {
      // Validation: Ensure restored IDs exist in loaded options
      const cachedGrades = regionSelectionsCache.value[newRegion].grades;
      const cachedSubjects = regionSelectionsCache.value[newRegion].subjects;

      const restoredGradeIds = cachedGrades.filter((id) =>
        gradeOptions.value.some((g) => g.value === id)
      );

      const restoredSubjectIds = cachedSubjects.filter((id) =>
        subjectOptions.value.some((s) => s.value === id)
      );

      selectedGrade.value = restoredGradeIds;
      selectedSubject.value = restoredSubjectIds;
    }
  }

  pendingSchoolObject.value = null;
  previousSchoolObject.value = null;
};

const handleCancelLocationChange = async () => {
  showLocationChangeModal.value = false;
  isRevertingSchool.value = true;
  selectedSchoolObject.value = previousSchoolObject.value;

  if (previousSchoolObject.value) {
    school.value = previousSchoolObject.value.name;
    selectedGrade.value = [...previousGrades.value];
    selectedSubject.value = [...previousSubjects.value];

    subjectOptions.value = [];
    gradeOptions.value = [];
    await Promise.all([loadSubjectOptions(), loadGradeOptions()]);
  }

  pendingSchoolObject.value = null;
  previousSchoolObject.value = null;
  previousGrades.value = [];
  previousSubjects.value = [];
};

/**
 * Initialize edit mode with user data.
 * Called by parent when entering edit mode.
 */
const initEdit = () => {
  if (props.user) {
    school.value = props.user.school;
    selectedSchoolObject.value = {
      name: props.user.school_object?.name,
      id: props.user.school_object?.id,
      location: getRegionCode(props.user.school_object?.locale),
    };

    // Initialize current valid object from user data
    currentValidSchoolObject.value = { ...selectedSchoolObject.value };

    // Reset Cache
    regionSelectionsCache.value = {};

    // Clear options and show loading
    gradeOptions.value = [];
    subjectOptions.value = [];

    Promise.all([loadGradeOptions(), loadSubjectOptions()]).then(() => {
      if (props.user.grades && props.user.grades.length > 0) {
        const selectedGradeIds = props.user.grades
          .map((gradeLabel) => {
            const grade = gradeOptions.value.find((g) => g.text === gradeLabel);
            return grade ? grade.value : null;
          })
          .filter((id) => id !== null);
        selectedGrade.value = selectedGradeIds;
      }

      if (props.user.subjects && props.user.subjects.length > 0) {
        const selectedSubjectIds = props.user.subjects
          .map((subjectLabel) => {
            const subject = subjectOptions.value.find(
              (s) => s.text === subjectLabel
            );
            return subject ? subject.value : null;
          })
          .filter((id) => id !== null);
        selectedSubject.value = selectedSubjectIds;
      }

      // Initialize cache for the starting region
      const startRegion = getRegionCode(
        currentValidSchoolObject.value?.location ||
          currentValidSchoolObject.value?.country ||
          "US"
      );
      regionSelectionsCache.value[startRegion] = {
        grades: [...selectedGrade.value],
        subjects: [...selectedSubject.value],
      };

      emit("editStarted");
    });
  }
};

/**
 * Returns the save data for the school section.
 * Called by parent when saving.
 */
const getSaveData = () => {
  const data = {};

  if (selectedSchoolObject.value?.name) {
    data.schoolObject = {
      title: selectedSchoolObject.value.name || school.value,
      country:
        selectedSchoolObject.value.country ||
        selectedSchoolObject.value.location ||
        "US",
    };
  } else if (props.user?.school_object || props.user?.school) {
    data.schoolObject = null;
  }

  data.subjects = selectedSubject.value
    .map((id) => getSubjectLabelFromId(id))
    .filter((label) => label !== null);
  data.grades = selectedGrade.value
    .map((id) => getGradeLabelFromId(id))
    .filter((label) => label !== null);

  return data;
};

defineExpose({
  initEdit,
  getSaveData,
});
</script>

<style scoped>
.section-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.section-header {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-xxl);
  line-height: 100%;
  color: #e00069;
  margin: 0;
}

.edit-school-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.school-inputs-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  margin-bottom: 0px !important;
}

.grade-label {
  color: #3b4648;
  font-size: var(--fs-large);
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin: 0 0 8px 0;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.chip-button {
  height: 44px;
  padding: 12px 20px;
  border: 1px solid #dfe2e7;
  border-radius: 24px;
  background-color: #ffffff;
  color: #40444b;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chip-button:hover {
  border-color: var(--primary-blue);
  background-color: #f2f3fb;
}

.chip-button.selected {
  background-color: #00355c;
  border-color: var(--primary-blue);
  color: #ffffff;
}

.chip-button.selected:hover {
  background-color: #002a4a;
  border-color: var(--primary-blue);
}

.chip-skeleton {
  height: 44px;
  padding: 12px 20px;
  border: 1px solid #dfe2e7;
  border-radius: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  min-width: 100px;
  max-width: 150px;
  width: 100%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.display-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-field {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.info-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--neutral-700);
  opacity: 0.5;
  margin: 0;
}

.info-value {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.error-message {
  color: #d6215a;
  font-size: var(--fs-medium);
  margin: 0;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .section-header {
    font-size: var(--fs-large);
    line-height: 28px;
    font-weight: 500;
  }

  .info-label {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  .info-value {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  .grade-label {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    font-weight: 400;
    margin-bottom: 4px;
    color: #3b4648;
  }

  .chip-button {
    height: 44px;
    padding: 12px 20px;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    font-weight: 700;
  }

  /* School Selection Input */
  .school-inputs-container :deep(.search-placeholder) {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    margin-bottom: 4px;
  }

  .school-inputs-container :deep(.school-input) {
    height: 40px;
    padding: 4px 16px 4px 48px;
    border-radius: 8px;
    border: 1px solid #6c737f;
    font-family: "League Spartan", sans-serif;
    font-weight: 500;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    box-sizing: border-box;
  }

  .school-inputs-container :deep(.input-with-icon) {
    height: 40px;
  }

  .school-inputs-container :deep(.school-selected-confirmation) {
    background-color: #e6f4ef;
    border: 1px solid #b0ddcd;
    border-radius: 8px;
    padding: 13px;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .school-inputs-container :deep(.confirmation-text) {
    font-family: "League Spartan", "Noto Sans", sans-serif;
    font-weight: 400;
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.035px;
    color: #00925e;
    margin: 0;
  }
}
</style>
